import {
    GridFilterItem,
    GridFilterModel,
    GridInitialState,
    GridLogicOperator,
    GridSortModel,
} from '@mui/x-data-grid-premium'

import {
    SavedSearchType,
    SearchScope,
    SearchScopeSchema,
    columnScopeKey,
} from 'Common/store/searchScopes'
import { logToService } from 'Common/utils/logging/log'

type LegacySearchOptions = {
    query?: string
    orderBy?: string
    combinator?: 'and' | 'or' | string
    [filterKey: string]: unknown
}

type LegacySavedFilter = {
    name: string
    searchOptions?: LegacySearchOptions
}

type LegacySavedColumns = {
    name: string
    activeColumns?: string[]
}

type LegacySurfaceConfig = {
    surface: string
    filterScope: SearchScope
    additionalFilterScopes?: SearchScope[]
    oldFilterKey: string
    oldColumnKey: string | null
    filterFieldMap?: Record<string, string>
    operatorByField?: Record<string, string>
    metadataKeyByField?: Record<string, string>
    ignoredFilterKeys?: string[]
    columnFieldMap?: Record<string, string>
    columnFieldTransform?: (field: string) => string
}

const LEGACY_SURFACES: LegacySurfaceConfig[] = [
    {
        surface: 'Edition',
        filterScope: 'searchStatesEdition',
        additionalFilterScopes: ['searchStatesEditionSalesSheet'],
        oldFilterKey: 'savedsearches-work-productionssearch-v5',
        oldColumnKey: 'savedviews-work-productionssearch-v2',
        filterFieldMap: {
            filterPublishingHouses: 'filterPublishingHouse',
            filterPublishingHouseGroups: 'filterPublishingHouseGroup',
            filterPublishingLists: 'filterPublishingList',
            filterBindings: 'filterBindingId',
            filterBindingCodes: 'filterBindingCode',
            filterBrands: 'filterBrand',
            filterCostCenters: 'filterCostCenter',
            filterArticleGroups: 'filterArticleGroup',
            filterBacklistSeasons: 'filterBacklistSeasonName',
            filterSeasons: 'filterSeasonName',
            filterThemaCodes: 'filterThemaCode',
            filterDiscountNumbers: 'filterDiscountNumber',
            filterInterestAges: 'filterInterestAge',
            filterMainGroups: 'filterMainGroupId',
            filterSubGroups: 'filterSubGroupId',
            filterEffortTypes: 'filterEffortTypeId',
            filterContractTypes: 'filterContractTypeId',
            filterInternalCategories: 'filterInternalCategoryId',
            filterListingCodes: 'filterListingCodeId',
            filterInsideFormats: 'filterInsideFormatId',
            filterTextTypes: 'filterTextTypeId',
            filterTechnicalProductionTypes: 'filterTechnicalProductionTypeId',
            filterProductionStatuses: 'filterProductionStatusId',
            filterSalesStatuses: 'filterSalesStatusId',
            filterSubjectTextTypes: 'filterTextTypeId',
            filterEbookIsAccessibleReadingEpub: 'filterActivePrintEbookIsAccessibleReadingEpub',
            filterAssetTypes: 'filterAssetTypeIds',
            filterAssetContractTypes: 'filterAssetContractTypeIds',
            filterHasAudioFile: 'filterActivePrintEbookHasAudioFile',
            filterExportDistribution: 'filterExportRulesDistributionSalesChannels',
            filterExportBlocked: 'filterExportRulesBlockedSalesChannels',
            filterExportRights: 'filterExportRulesRightsSalesChannels',
            filterHasFscPaper: 'filterActivePrintHasFscPaper',
            filterhasIsbn: 'filterHasIsbn',
            filterIsBgaActive: 'filterIsAgencyActive',
            filterEffortType: 'filterEffortTypeId',
            filterMissingAssetTypes: 'filterMissingAssetTypeIds',
            filterPubDates: 'filterPublishingDate',
            filterRevDates: 'filterReviewDate',
            filterRoyalty: 'filterRequiresRoyaltyAgreement',
            filterStatuses: 'filterProductionStatusId',
        },
        ignoredFilterKeys: ['filterSalesYear'],
        operatorByField: {
            filterActivePrintEbookIsAccessibleReadingEpub: 'has',
            filterActiveWebPeriodEndDate: 'date',
            filterActiveWebPeriodStartDate: 'date',
            filterInterestAge: 'is',
            filterAssetTypeIds: 'in',
            filterAssetContractTypeIds: 'in',
            filterActivePrintEbookHasAudioFile: 'has',
            filterBacklistSeasonName: 'season',
            filterBindingId: 'is',
            filterBindingCode: 'is',
            filterBindingCodeType: 'is',
            filterBrand: 'is',
            filterHasCeLogo: 'has',
            filterIsConnectedToERP: 'has',
            filterContacts: 'contactAndRole',
            filterCostCenter: 'is',
            filterCreatedDate: 'date',
            filterIsCancelled: 'has',
            filterExportRulesDistributionSalesChannels: 'in',
            filterExportRulesBlockedSalesChannels: 'in',
            filterExportRulesRightsSalesChannels: 'in',
            filterActivePrintHasFscPaper: 'has',
            filterListingCodeId: 'is',
            filterInsideFormatId: 'is',
            filterInternalCategoryId: 'is',
            filterHasIsbn: 'has',
            filterIsAgencyActive: 'has',
            filterIsIllustrated: 'has',
            filterLanguageIds: 'in',
            filterEffortTypeId: 'is',
            filterIsMainEdition: 'has',
            filterIsWebEdition: 'has',
            filterMainGroupId: 'is',
            filterMissingAssetTypeIds: 'in',
            filterIsPrintOnDemand: 'has',
            filterArticleGroup: 'is',
            filterIsPublished: 'has',
            filterPublishingDate: 'date',
            filterPublishingHouse: 'is',
            filterPublishingHouseGroup: 'is',
            filterPublishingList: 'is',
            filterReviewDate: 'date',
            filterRequiresRoyaltyAgreement: 'has',
            filterContractTypeId: 'is',
            filterSalesStatusId: 'is',
            filterDiscountNumber: 'is',
            filterSeasonName: 'season',
            filterBgaShowOnSite: 'has',
            filterProductionStatusId: 'is',
            filterSubGroupId: 'is',
            filterTechnicalProductionTypeId: 'is',
            filterThemaCode: 'thema',
            filterTextTypeId: 'is',
            filterTimeplan: 'timeplan',
            filterUpdatedDate: 'date',
            filterProhibitTextAndDataMining: 'has',
        },
        metadataKeyByField: {
            filterInterestAge: 'interestAges',
            filterAssetTypeIds: 'assetTypes',
            filterAssetContractTypeIds: 'assetContractTypes',
            filterBindingId: 'bookBindingBindings',
            filterBindingCode: 'bindingCodes',
            filterBrand: 'brands',
            filterCostCenter: 'costCenters',
            filterExportRulesDistributionSalesChannels: 'salesChannels',
            filterExportRulesBlockedSalesChannels: 'salesChannels',
            filterExportRulesRightsSalesChannels: 'salesChannels',
            filterListingCodeId: 'listingCodes',
            filterInsideFormatId: 'contractFormats',
            filterInternalCategoryId: 'internalCategories',
            filterLanguageIds: 'productionLanguages',
            filterEffortTypeId: 'effortTypes',
            filterMainGroupId: 'mainGroups',
            filterMissingAssetTypeIds: 'assetTypes',
            filterArticleGroup: 'productGroups',
            filterPublishingHouse: 'publishingHouses',
            filterPublishingHouseGroup: 'publishingHouseGroups',
            filterPublishingList: 'publishingLists',
            filterContractTypeId: 'contractTypes',
            filterSalesStatusId: 'salesStatus',
            filterDiscountNumber: 'discountNumbers',
            filterProductionStatusId: 'productionStatuses',
            filterSubGroupId: 'subGroups',
            filterTechnicalProductionTypeId: 'technicalProductionTypes',
            filterTextTypeId: 'textTypes',
        },
    },
    {
        surface: 'Asset',
        filterScope: 'searchStatesAsset',
        oldFilterKey: 'savedsearches-assets-assetssearch-v1',
        oldColumnKey: null,
        filterFieldMap: {
            filterPublishingHouses: 'filterPublishingHouseId',
            filterPublishingHouseGroups: 'filterPublishingHouseGroupId',
            productionBindingCodeIds: 'filterProductionBindingCodeIds',
        },
        operatorByField: {
            filterAssetTypeId: 'is',
            filterContractTypeId: 'is',
            filterPublishingHouseId: 'is',
            filterPublishingHouseGroupId: 'is',
            filterProductionBindingCodeIds: 'in',
            filterContractDate: 'date',
            filterDateUploaded: 'date',
        },
        metadataKeyByField: {
            filterAssetTypeId: 'assetTypes',
            filterContractTypeId: 'assetContractTypes',
            filterPublishingHouseId: 'publishingHouses',
            filterPublishingHouseGroupId: 'publishingHouseGroups',
            filterProductionBindingCodeIds: 'bindingCodes',
        },
    },
    {
        surface: 'Contact',
        filterScope: 'searchStatesContact',
        oldFilterKey: 'savedsearches-contact-contactssearch-v2',
        oldColumnKey: 'savedviews-contact-contactssearch-v1',
        filterFieldMap: {
            filterCustomerGroup: 'filterCustomerGroupId',
            filterCustomerSubGroup: 'filterCustomerSubGroupId',
            filterCustomerGroups: 'filterCustomerGroupId',
            filterCustomerSubGroups: 'filterCustomerSubGroupId',
            filterPublishingHouses: 'filterPublishingHouse',
            filterBindingCodes: 'filterBindingCode',
            filterBrands: 'filterBrand',
            filterPubDates: 'filterPublishingDate',
            filterSeasons: 'filterSeasonName',
            filterHasErpSupplierId: 'filterErpSupplierId',
        },
        ignoredFilterKeys: ['filterhasGdprSecuredNotes'],
        operatorByField: {
            filterCustomerSubGroupId: 'is',
            filterCustomerGroupId: 'is',
            filterBirthDay: 'date',
            filterIsUser: 'has',
            filterIsCompanyContact: 'has',
            filterHasContactPresentation: 'has',
            filterHasAuthorPhotographStandard: 'has',
            filterHasAuthorPhotographDownload: 'has',
            filterIsSecret: 'has',
            filterErpSupplierId: 'missing',
            filterPublishingDate: 'date',
            filterBindingCode: 'is',
            filterBrand: 'is',
            filterSeasonName: 'season',
            filterPublishingHouse: 'is',
            filterListIds: 'in',
            filterRoles: 'is',
        },
        metadataKeyByField: {
            filterCustomerSubGroupId: 'customerSubGroups',
            filterCustomerGroupId: 'customerGroups',
            filterBindingCode: 'bindingCodes',
            filterBrand: 'brands',
            filterPublishingHouse: 'publishingHouses',
            filterListIds: 'selectionLists',
            filterRoles: 'contactRoles',
        },
    },
    {
        surface: 'Concept',
        filterScope: 'searchStatesConcept',
        oldFilterKey: 'savedsearches-concept-conceptssearch-v2',
        oldColumnKey: 'savedviews-concept-conceptssearch-v1',
        filterFieldMap: {
            filterSeasons: 'filterSeasonName',
            filterThemaCodes: 'filterThemaCode',
            filterConceptStatuses: 'filterStatus',
            filterPublishingLists: 'filterPublishingList',
            filterPublishingHouseGroups: 'filterPublishingHouseGroup',
            filterPublishingHouses: 'filterPublishingHouse',
        },
        operatorByField: {
            filterStatus: 'is',
            filterSeasonName: 'season',
            filterPublishingHouseGroup: 'is',
            filterPublishingHouse: 'is',
            filterPublishingList: 'is',
            filterThemaCode: 'thema',
            filterContacts: 'contactAndRole',
            filterCostCenter: 'is',
        },
        metadataKeyByField: {
            filterStatus: 'conceptStatuses',
            filterPublishingHouseGroup: 'publishingHouseGroups',
            filterPublishingHouse: 'publishingHouses',
            filterPublishingList: 'publishingLists',
            filterCostCenter: 'costCenters',
        },
        columnFieldMap: {
            themas: 'themasDescriptions',
            themasFull: 'themas',
            plannedSeason: 'seasonName',
        },
    },
    {
        surface: 'Activity',
        filterScope: 'searchStatesActivity',
        oldFilterKey: 'savedsearches-work-activities-v1',
        oldColumnKey: 'savedviews-work-activities-v1',
        filterFieldMap: {
            filterCategories: 'filterCategoryId',
            filterStatuses: 'filterStatusId',
            filterSharingLevels: 'filterSharingLevelId',
            filterRegDates: 'filterRegistrationDate',
            filterPubDates: 'filterPublishingDate',
            filterMainEditionPublishingHouses: 'filterMainEditionPublishingHouseId',
            filterMainEditionSeasons: 'filterMainEditionSeasonName',
        },
        operatorByField: {
            filterCategoryId: 'is',
            filterStatusId: 'is',
            filterSharingLevelId: 'is',
            filterMainEditionPublishingHouseId: 'is',
            filterMainEditionBrand: 'is',
            filterMainEditionPublishingDate: 'date',
            filterMainEditionSeasonName: 'season',
            filterActivityStartDate: 'date',
            filterActivityEndDate: 'date',
            filterPublishingDate: 'date',
            filterRegistrationDate: 'date',
        },
        metadataKeyByField: {
            filterCategoryId: 'activityCategories',
            filterStatusId: 'activityStatus',
            filterSharingLevelId: 'activitySharingLevels',
            filterMainEditionPublishingHouseId: 'publishingHouses',
            filterMainEditionBrand: 'brands',
        },
    },
    {
        surface: 'Work',
        filterScope: 'searchStatesWork',
        additionalFilterScopes: ['searchStatesWorkLaunchBrief'],
        oldFilterKey: 'savedsearches-work-works-v1',
        oldColumnKey: 'savedviews-work-works-v1',
        filterFieldMap: {
            filterMainEditionPublishingHouses: 'filterPublishingHouseId',
            filterPublishingHouses: 'filterPublishingHouseId',
            filterPublishingHouseGroups: 'filterPublishingHouseGroup',
            filterPublishingLists: 'filterPublishingList',
            filterCostCenters: 'filterCostCenter',
            filterMainEditionInternalCategory: 'filterMainEditionInternalCategoryId',
            filterMainEditionInternalCategories: 'filterMainEditionInternalCategoryId',
            filterMainEditionEffortType: 'filterMainEditionEffortTypeId',
            filterMainEditionEffortTypes: 'filterMainEditionEffortTypeId',
            filterMainEditionMainGroup: 'filterMainEditionMainGroupId',
            filterMainEditionMainGroups: 'filterMainEditionMainGroupId',
            filterMainEditionSubGroup: 'filterMainEditionSubGroupId',
            filterMainEditionSubGroups: 'filterMainEditionSubGroupId',
            filterMainEditionInterestAges: 'filterMainEditionInterestAge',
            filterMainEditionSeasons: 'filterMainEditionSeasonName',
            filterOriginalLanguages: 'filterOriginalLanguageIds',
            filterThemaCodes: 'filterThemaCode',
            filterisAuthorAvailableForPress: 'filterIsAuthorAvailableForPress',
            filtermMainEditionPubDates: 'filterMainEditionPublishingDate',
            filtermMainEditionRevDates: 'filterMainEditionReviewDate',
        },
        operatorByField: {
            filterContacts: 'contactAndRole',
            filterCostCenter: 'is',
            filterIsAuthorAvailableForEvents: 'has',
            filterIsAuthorAvailableForPress: 'has',
            filterIsCancelled: 'has',
            filterMainEditionInterestAge: 'is',
            filterMainEditionInternalCategoryId: 'is',
            filterMainEditionEffortTypeId: 'is',
            filterMainEditionMainGroupId: 'is',
            filterMainEditionPublishingDate: 'date',
            filterMainEditionReviewDate: 'date',
            filterMainEditionSeasonName: 'season',
            filterMainEditionSubGroupId: 'is',
            filterOriginalLanguageIds: 'in',
            filterPublishingHouseId: 'is',
            filterPublishingHouseGroup: 'is',
            filterPublishingList: 'is',
            filterThemaCode: 'thema',
            filterWillAuthorParticipate: 'has',
        },
        metadataKeyByField: {
            filterCostCenter: 'costCenters',
            filterMainEditionInterestAge: 'interestAges',
            filterMainEditionInternalCategoryId: 'internalCategories',
            filterMainEditionEffortTypeId: 'effortTypes',
            filterMainEditionMainGroupId: 'mainGroups',
            filterMainEditionSubGroupId: 'subGroups',
            filterOriginalLanguageIds: 'productionLanguages',
            filterPublishingHouseId: 'publishingHouses',
            filterPublishingHouseGroup: 'publishingHouseGroups',
            filterPublishingList: 'publishingLists',
        },
        columnFieldMap: {
            themasFull: 'themas',
            effortTypeName: 'mainEditionEffortTypeName',
        },
        columnFieldTransform: (field) =>
            field.startsWith('textFullByTypes_') ? `mainEdition_${field}` : field,
    },
]

function parseList<T>(raw: unknown): T[] {
    if (!raw) return []
    let arr: unknown
    if (Array.isArray(raw)) {
        arr = raw
    } else if (typeof raw === 'string') {
        try {
            arr = JSON.parse(raw)
        } catch {
            return []
        }
    } else {
        return []
    }
    if (!Array.isArray(arr)) return []
    return arr.filter((entry): entry is T => entry != null && typeof entry === 'object')
}

function isPrimitive(v: unknown): v is string | number | boolean {
    const t = typeof v
    return t === 'string' || t === 'number' || t === 'boolean'
}

function extractBoolean(raw: unknown): boolean | undefined {
    if (typeof raw === 'boolean') return raw
    if (raw === 'true') return true
    if (raw === 'false') return false
    if (Array.isArray(raw)) {
        for (const v of raw) {
            const r = extractBoolean(v)
            if (r !== undefined) return r
        }
        return undefined
    }
    if (raw && typeof raw === 'object') {
        const obj = raw as Record<string, unknown>
        for (const prop of ['value', 'id', 'selected']) {
            const v = obj[prop]
            if (typeof v === 'boolean') return v
            if (v === 'true') return true
            if (v === 'false') return false
        }
    }
    return undefined
}

function parseOrderBy(orderBy: string | undefined): GridSortModel {
    if (!orderBy) return []
    const trimmed = orderBy.trim()
    if (!trimmed || trimmed === 'relevance') return []
    const [field, dir] = trimmed.split(/\s+/)
    if (!field) return []
    return [{ field, sort: dir === 'desc' ? 'desc' : 'asc' }]
}

function convertColumnKey(value: string): string {
    if (value === 'contactId') return 'id'

    const timeplan = value.match(/^timeplan-(actual|planned)-(\d+)$/)
    if (timeplan) {
        const [, type, num] = timeplan
        return `activePrintTimeplan${num}${type[0].toUpperCase()}${type.slice(1)}`
    }

    const address = value.match(/^address-([^-]+)-(.+)$/)
    if (address) {
        const [, type, field] = address
        return `${field}${type}`
    }

    const text = value.match(/^text-(\d+)$/)
    if (text) {
        const [, num] = text
        return `textFullByTypes_${num}`
    }

    return value
}

type Diagnostic = { name: string; key: string; reason: string }

function toIdObject(v: unknown): { id: unknown } | null {
    if (isPrimitive(v)) return { id: v }
    if (v && typeof v === 'object') {
        const obj = v as Record<string, unknown>
        if ('id' in obj) return obj as { id: unknown }
        if ('roleId' in obj) return { ...obj, id: obj.roleId }
    }
    return null
}

function toIdObjects(value: unknown): Array<{ id: unknown }> | null {
    if (Array.isArray(value)) {
        const ids = value.map(toIdObject)
        if (ids.some((v) => v == null)) return null
        return ids as Array<{ id: unknown }>
    }
    const single = toIdObject(value)
    return single ? [single] : null
}

type DraftItem = Omit<GridFilterItem, 'id'>

function buildFilterItems(
    field: string,
    operator: string,
    rawValue: unknown,
    legacyName: string,
    legacyKey: string,
    diagnostics: Diagnostic[],
): DraftItem[] {
    switch (operator) {
        case 'is':
        case 'in': {
            const value = toIdObjects(rawValue)
            if (!value) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `cannot shape ${JSON.stringify(rawValue).slice(0, 60)} as id-objects for ${operator}`,
                })
                return []
            }
            return [{ field, operator, value }]
        }
        case 'has':
        case 'missing': {
            const bool = extractBoolean(rawValue)
            if (bool === undefined) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `could not extract boolean for ${operator} from ${JSON.stringify(rawValue).slice(0, 80)}`,
                })
                return []
            }
            return [{ field, operator, value: bool ? 'true' : 'false' }]
        }
        case 'thema': {
            if (!Array.isArray(rawValue)) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `expected array for thema filter, got ${typeof rawValue}`,
                })
                return []
            }
            const value = rawValue.filter((v) => v && typeof v === 'object')
            if (value.length === 0) return []
            return [{ field, operator: 'in', value }]
        }
        case 'season': {
            if (!Array.isArray(rawValue)) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `expected array of {year, season?} for season filter, got ${typeof rawValue}`,
                })
                return []
            }
            const value: Array<{ id: string; name: string }> = []
            for (const entry of rawValue) {
                if (!entry || typeof entry !== 'object') continue
                const { year, season } = entry as { year?: string; season?: string }
                if (typeof year !== 'string' || year.length === 0) continue
                const period = typeof season === 'string' ? season : ''
                const id = period ? `${year} ${period}` : year
                value.push({ id, name: id })
            }
            if (value.length === 0) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `no usable {year} entries in season array`,
                })
                return []
            }
            return [{ field, operator: 'is', value }]
        }
        case 'contactAndRole': {
            if (!Array.isArray(rawValue)) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `expected array for contactAndRole filter, got ${typeof rawValue}`,
                })
                return []
            }
            const value = rawValue
                .map((entry) => {
                    if (!entry || typeof entry !== 'object') return null
                    const { contactId, roleId, contactName } = entry as {
                        contactId?: unknown
                        roleId?: unknown
                        contactName?: unknown
                    }
                    if (contactId == null && roleId == null) return null
                    return {
                        contact:
                            contactId != null
                                ? {
                                      id: contactId,
                                      name: typeof contactName === 'string' ? contactName : '',
                                  }
                                : null,
                        role: roleId != null ? { id: roleId, name: '' } : null,
                    }
                })
                .filter(Boolean)
            if (value.length === 0) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `no usable {contactId|roleId} entries in contactAndRole array`,
                })
                return []
            }
            return [{ field, operator: 'is', value }]
        }
        case 'timeplan': {
            if (!Array.isArray(rawValue)) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `expected array for timeplan filter, got ${typeof rawValue}`,
                })
                return []
            }
            const entries: Record<string, string>[] = []
            for (const entry of rawValue) {
                if (!entry || typeof entry !== 'object') continue
                const { min, max, activityId, activityDateType } = entry as {
                    min?: string
                    max?: string
                    activityId?: string
                    activityDateType?: string
                }
                if (!activityId || !activityDateType || !min) continue
                const value: Record<string, string> = {
                    activityId: String(activityId),
                    activityDateType,
                    start: min,
                }
                if (typeof max === 'string' && max.length > 0) value.end = max
                entries.push(value)
            }
            if (entries.length === 0) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `no usable {activityId, activityDateType, min} entries in timeplan array`,
                })
                return []
            }
            return [{ field, operator: 'timeplan', value: entries }]
        }
        case 'date': {
            if (!Array.isArray(rawValue)) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `expected array of {min, max?} for date filter, got ${typeof rawValue}`,
                })
                return []
            }
            const items: DraftItem[] = []
            for (const entry of rawValue) {
                if (!entry || typeof entry !== 'object') continue
                const { min, max } = entry as { min?: string; max?: string }
                if (typeof min !== 'string' || min.length === 0) continue
                if (typeof max === 'string' && max.length > 0) {
                    items.push({
                        field,
                        operator: 'dateRange',
                        value: { start: min, end: max },
                    })
                } else {
                    items.push({
                        field,
                        operator: 'date',
                        value: { start: min, end: '' },
                    })
                }
            }
            if (items.length === 0) {
                diagnostics.push({
                    name: legacyName,
                    key: legacyKey,
                    reason: `no usable {min} entries in date array`,
                })
            }
            return items
        }
        default: {
            diagnostics.push({
                name: legacyName,
                key: legacyKey,
                reason: `operator "${operator}" not yet handled by migration`,
            })
            return []
        }
    }
}

function convertOldFilter(
    legacy: LegacySavedFilter,
    cfg: LegacySurfaceConfig,
    diagnostics: Diagnostic[],
): SavedSearchType {
    const opts = legacy.searchOptions ?? {}
    const fieldMap = cfg.filterFieldMap ?? {}
    const opMap = cfg.operatorByField ?? {}
    const ignored = new Set(cfg.ignoredFilterKeys ?? [])
    const items: GridFilterItem[] = []
    let nextId = 1

    for (const [key, value] of Object.entries(opts)) {
        if (!key.startsWith('filter') && !(key in fieldMap)) continue
        if (ignored.has(key)) continue
        if (value == null) continue
        if (Array.isArray(value) && value.length === 0) continue

        const field = fieldMap[key] ?? key
        const operator = opMap[field]

        if (!operator) {
            diagnostics.push({
                name: legacy.name,
                key,
                reason: `no operator mapping for field "${field}" — add to operatorByField`,
            })
            continue
        }

        const drafts = buildFilterItems(field, operator, value, legacy.name, key, diagnostics)
        for (const draft of drafts) {
            items.push({ id: nextId++, ...draft })
        }
    }

    const quickFilterLogicOperator =
        opts.combinator === 'or' ? GridLogicOperator.Or : GridLogicOperator.And
    const filterModel: GridFilterModel = {
        items,
        logicOperator: GridLogicOperator.And,
        quickFilterLogicOperator,
    }
    if (typeof opts.query === 'string' && opts.query.trim().length > 0) {
        filterModel.quickFilterValues = [opts.query.trim()]
    }

    const sortModel = parseOrderBy(opts.orderBy)

    const state: GridInitialState = {
        filter: { filterModel },
        sorting: { sortModel },
    }

    return { name: legacy.name, state }
}

function convertOldColumns(
    legacy: LegacySavedColumns,
    cfg: LegacySurfaceConfig,
): SavedSearchType | null {
    const cols = legacy.activeColumns
    if (!Array.isArray(cols) || cols.length === 0) return null

    const surfaceMap = cfg.columnFieldMap ?? {}
    const transform = cfg.columnFieldTransform
    const visibility: Record<string, boolean> = {}
    const orderedFields: string[] = []
    for (const col of cols) {
        const renamed = surfaceMap[col] ?? convertColumnKey(col)
        const final = transform ? transform(renamed) : renamed
        visibility[final] = true
        if (!orderedFields.includes(final)) orderedFields.push(final)
    }

    const state: GridInitialState = {
        columns: { columnVisibilityModel: visibility, orderedFields },
    }

    return { name: legacy.name, state }
}

export function hydrateLegacyFilterValues(
    state: GridInitialState | null | undefined,
    scope: SearchScope,
    metadata: unknown,
): GridInitialState | null | undefined {
    if (!state?.filter?.filterModel?.items?.length) return state
    if (!metadata || typeof metadata !== 'object') return state

    const cfg = LEGACY_SURFACES.find((s) => s.filterScope === scope)
    const map = cfg?.metadataKeyByField ?? {}

    const contactRoles = (metadata as Record<string, unknown>).contactRoles
    const contactRoleList = Array.isArray(contactRoles)
        ? (contactRoles as Array<{ id: unknown; name?: unknown }>)
        : null

    const timePlanEntryTypes = (metadata as Record<string, unknown>).timePlanEntryTypes
    const timeplanList = Array.isArray(timePlanEntryTypes)
        ? (timePlanEntryTypes as Array<{ id: unknown; name?: unknown }>)
        : null

    const items = state.filter.filterModel.items.map((rawItem) => {
        const item =
            rawItem.field === 'filterIsBgaActive'
                ? { ...rawItem, field: 'filterIsAgencyActive' }
                : rawItem
        if (timeplanList && item.operator === 'timeplan' && item.value) {
            const hydrateEntry = (entry: unknown) => {
                if (!entry || typeof entry !== 'object') return entry
                const v = entry as { activityId?: unknown; activityName?: unknown }
                if (v.activityId == null || v.activityName) return entry
                const match =
                    timeplanList.find((m) => m.id === v.activityId) ??
                    timeplanList.find((m) => String(m.id) === String(v.activityId))
                return match?.name ? { ...entry, activityName: match.name } : entry
            }
            if (Array.isArray(item.value)) {
                let changed = false
                const hydrated = item.value.map((entry) => {
                    const next = hydrateEntry(entry)
                    if (next !== entry) changed = true
                    return next
                })
                if (changed) return { ...item, value: hydrated }
            } else if (typeof item.value === 'object') {
                const next = hydrateEntry(item.value)
                if (next !== item.value) return { ...item, value: next }
            }
        }

        if (
            contactRoleList &&
            Array.isArray(item.value) &&
            item.value.some((v) => v && typeof v === 'object' && ('contact' in v || 'role' in v))
        ) {
            let changed = false
            const hydrated = item.value.map((entry) => {
                if (!entry || typeof entry !== 'object') return entry
                const obj = entry as { contact?: unknown; role?: unknown }
                const role = obj.role as { id?: unknown; name?: unknown } | null | undefined
                if (!role || role.id == null) return entry
                if (typeof role.name === 'string' && role.name.length > 0) return entry
                const match =
                    contactRoleList.find((m) => m.id === role.id) ??
                    contactRoleList.find((m) => String(m.id) === String(role.id))
                if (!match) return entry
                changed = true
                return { ...obj, role: { ...role, ...match } }
            })
            if (changed) return { ...item, value: hydrated }
        }

        const metadataKey = map[item.field]
        if (!metadataKey) return item
        if (!Array.isArray(item.value)) return item

        const lookup = (metadata as Record<string, unknown>)[metadataKey]
        if (!Array.isArray(lookup)) return item

        let changed = false
        const hydrated = item.value.map((entry) => {
            if (!entry || typeof entry !== 'object') return entry
            const obj = entry as { id?: unknown; name?: unknown }
            const list = lookup as Array<{ id: unknown }>
            const match =
                list.find((m) => m && (m as { id: unknown }).id === obj.id) ??
                list.find((m) => m && String((m as { id: unknown }).id) === String(obj.id))
            if (!match) return entry
            if (match === entry) return entry
            changed = true
            return match
        })

        return changed ? { ...item, value: hydrated } : item
    })

    return {
        ...state,
        filter: {
            ...state.filter,
            filterModel: {
                ...state.filter.filterModel,
                items,
            },
        },
    }
}

function mergeByName(existing: SavedSearchType[], incoming: SavedSearchType[]): SavedSearchType[] {
    const names = new Set(existing.map((e) => e.name))
    const filtered = incoming.filter((e) => !names.has(e.name))
    return [...existing, ...filtered]
}

function mergeColumnsByName(
    existing: SavedSearchType[],
    incoming: SavedSearchType[],
): SavedSearchType[] {
    const incomingByName = new Map(incoming.map((e) => [e.name, e]))
    const seen = new Set<string>()

    const updatedExisting = existing.map((e) => {
        seen.add(e.name)
        const incomingEntry = incomingByName.get(e.name)
        if (!incomingEntry) return e
        const existingOrder = e.state?.columns?.orderedFields
        if (existingOrder && existingOrder.length > 0) return e
        return {
            ...e,
            state: {
                ...e.state,
                columns: {
                    ...(e.state?.columns ?? {}),
                    orderedFields: incomingEntry.state?.columns?.orderedFields ?? [],
                },
            },
        }
    })

    const newOnes = incoming.filter((e) => !seen.has(e.name))
    return [...updatedExisting, ...newOnes]
}

export type LegacyMigrationResult = {
    next: Partial<Record<string, SavedSearchType[]>>
    pendingPosts: Array<[key: string, value: SavedSearchType[]]>
    didMigrate: boolean
}

export type LegacyMigrationOptions = {
    hideAgeAndSubGroup?: boolean
}

const HIDDEN_AGE_AND_SUBGROUP_KEYS_BY_SURFACE: Record<string, string[]> = {
    Edition: ['filterInterestAges', 'filterInterestAge', 'filterSubGroups', 'filterSubGroupId'],
    Work: [
        'filterMainEditionInterestAges',
        'filterMainEditionInterestAge',
        'filterMainEditionSubGroups',
        'filterMainEditionSubGroup',
        'filterMainEditionSubGroupId',
    ],
}

export function migrateLegacyOldSaves(
    settings: Partial<Record<string, SavedSearchType[]>>,
    options: LegacyMigrationOptions = {},
): LegacyMigrationResult {
    const next: Partial<Record<string, SavedSearchType[]>> = { ...settings }
    const pendingPosts: Array<[string, SavedSearchType[]]> = []
    let didMigrate = false

    for (const baseCfg of LEGACY_SURFACES) {
        const extraIgnored = options.hideAgeAndSubGroup
            ? (HIDDEN_AGE_AND_SUBGROUP_KEYS_BY_SURFACE[baseCfg.surface] ?? [])
            : []
        const cfg: LegacySurfaceConfig = extraIgnored.length
            ? {
                  ...baseCfg,
                  ignoredFilterKeys: [...(baseCfg.ignoredFilterKeys ?? []), ...extraIgnored],
              }
            : baseCfg
        const oldFilters = parseList<LegacySavedFilter>(settings[cfg.oldFilterKey])
        const oldColumns = cfg.oldColumnKey
            ? parseList<LegacySavedColumns>(settings[cfg.oldColumnKey])
            : []

        if (oldFilters.length === 0 && oldColumns.length === 0) continue
        didMigrate = true

        const filterDiagnostics: Diagnostic[] = []

        if (oldFilters.length > 0) {
            const converted: SavedSearchType[] = []
            for (const f of oldFilters) {
                const before = filterDiagnostics.length
                const result = convertOldFilter(f, cfg, filterDiagnostics)
                const status = filterDiagnostics.length === before ? 'success' : 'partial'
                logToService({
                    name: 'LegacySaveMigration',
                    message: JSON.stringify({
                        type: 'filter',
                        key: cfg.oldFilterKey,
                        savedName: f.name,
                        status,
                        searchOptions: f.searchOptions ?? {},
                    }),
                }).catch(() => {})
                converted.push(result)
            }
            const merged = mergeByName(parseList<SavedSearchType>(next[cfg.filterScope]), converted)
            next[cfg.filterScope] = merged
            pendingPosts.push([cfg.filterScope, merged])

            for (const extraScope of cfg.additionalFilterScopes ?? []) {
                const extraMerged = mergeByName(
                    parseList<SavedSearchType>(next[extraScope]),
                    converted,
                )
                next[extraScope] = extraMerged
                pendingPosts.push([extraScope, extraMerged])
            }

            const skipped = filterDiagnostics.length
            console.info(
                `[saved-view migration] ${cfg.surface}: ${converted.length} filter(s) migrated` +
                    (skipped > 0 ? `; ${skipped} field value(s) skipped — see warnings` : ''),
            )
            for (const d of filterDiagnostics) {
                console.warn(
                    `[saved-view migration] ${cfg.surface} "${d.name}": dropped ${d.key} (${d.reason})`,
                )
            }
        }

        if (cfg.oldColumnKey && oldColumns.length > 0) {
            const newColumnKey = columnScopeKey(cfg.filterScope)
            const converted: SavedSearchType[] = []
            for (const c of oldColumns) {
                const result = convertOldColumns(c, cfg)
                const status = result !== null ? 'success' : 'failed'
                logToService({
                    name: 'LegacySaveMigration',
                    message: JSON.stringify({
                        type: 'column',
                        key: cfg.oldColumnKey,
                        savedName: c.name,
                        status,
                        activeColumns: c.activeColumns ?? [],
                    }),
                }).catch(() => {})
                if (result) converted.push(result)
            }
            const merged = mergeColumnsByName(
                parseList<SavedSearchType>(next[newColumnKey]),
                converted,
            )
            next[newColumnKey] = merged
            pendingPosts.push([newColumnKey, merged])

            console.info(
                `[saved-view migration] ${cfg.surface}: ${converted.length} column layout(s) migrated`,
            )
        }

        next[cfg.oldFilterKey] = []
        pendingPosts.push([cfg.oldFilterKey, []])
        if (cfg.oldColumnKey) {
            next[cfg.oldColumnKey] = []
            pendingPosts.push([cfg.oldColumnKey, []])
        }
    }

    return { next, pendingPosts, didMigrate }
}

const SEASON_FILTER_FIELDS = new Set([
    'filterSeasonName',
    'filterMainEditionSeasonName',
    'filterBacklistSeasonName',
])

type SeasonOption = { id: string; name: string }

function extractSeasonOptions(item: GridFilterItem): SeasonOption[] | null {
    if (!SEASON_FILTER_FIELDS.has(item.field)) return null
    const v = item.value
    if (!v) return null
    if (Array.isArray(v)) {
        const options = v
            .map((entry) => {
                if (!entry || typeof entry !== 'object') return null
                const obj = entry as { id?: unknown; name?: unknown }
                if (typeof obj.id !== 'string' || obj.id.length === 0) return null
                const name = typeof obj.name === 'string' && obj.name.length > 0 ? obj.name : obj.id
                return { id: obj.id, name } as SeasonOption
            })
            .filter((o): o is SeasonOption => o != null)
        return options.length > 0 ? options : null
    }
    if (typeof v !== 'object') return null
    const { year, period } = v as { year?: unknown; period?: unknown }
    if (typeof year !== 'string' || year.length === 0) return null
    const p = typeof period === 'string' ? period : ''
    const id = p ? `${year} ${p}` : year
    return [{ id, name: id }]
}

function isAlreadyMergedSeasonItem(item: GridFilterItem, options: SeasonOption[]): boolean {
    if (item.operator !== 'is') return false
    if (!Array.isArray(item.value) || item.value.length !== options.length) return false
    return item.value.every((entry, i) => {
        if (!entry || typeof entry !== 'object') return false
        const obj = entry as { id?: unknown; name?: unknown }
        return obj.id === options[i].id && obj.name === options[i].name
    })
}

function mergeSeasonItems(items: GridFilterItem[]): GridFilterItem[] | null {
    const seasonByField = new Map<string, { values: SeasonOption[]; count: number }>()
    const result: GridFilterItem[] = []
    let changed = false

    for (const item of items) {
        const options = extractSeasonOptions(item)
        if (!options) {
            result.push(item)
            continue
        }
        const existing = seasonByField.get(item.field)
        if (existing) {
            for (const option of options) {
                if (!existing.values.some((v) => v.id === option.id)) {
                    existing.values.push(option)
                }
            }
            existing.count += 1
            changed = true
            continue
        }
        const placeholder = { ...item, operator: 'is', value: options } as GridFilterItem
        seasonByField.set(item.field, { values: options, count: 1 })
        result.push(placeholder)
        if (!isAlreadyMergedSeasonItem(item, options)) changed = true
    }

    for (const item of result) {
        const entry = seasonByField.get(item.field)
        if (entry) (item as { value: unknown }).value = entry.values
    }

    return changed ? result : null
}

export function upgradeSeasonFilterShape(settings: Partial<Record<string, SavedSearchType[]>>): {
    next: Partial<Record<string, SavedSearchType[]>>
    pendingPosts: Array<[string, SavedSearchType[]]>
} {
    const next: Partial<Record<string, SavedSearchType[]>> = { ...settings }
    const pendingPosts: Array<[string, SavedSearchType[]]> = []

    for (const scope of SearchScopeSchema.options) {
        const list = parseList<SavedSearchType>(settings[scope])
        if (list.length === 0) continue

        let scopeChanged = false
        const upgraded = list.map((entry) => {
            const items = entry?.state?.filter?.filterModel?.items
            if (!Array.isArray(items) || items.length === 0) return entry
            const merged = mergeSeasonItems(items)
            if (!merged) return entry
            scopeChanged = true
            return {
                ...entry,
                state: {
                    ...entry.state,
                    filter: {
                        ...entry.state!.filter,
                        filterModel: {
                            ...entry.state!.filter!.filterModel!,
                            items: merged,
                        },
                    },
                },
            }
        })

        if (scopeChanged) {
            next[scope] = upgraded
            pendingPosts.push([scope, upgraded])
        }
    }

    return { next, pendingPosts }
}

export function upgradeBgaActiveFilterField(settings: Partial<Record<string, SavedSearchType[]>>): {
    next: Partial<Record<string, SavedSearchType[]>>
    pendingPosts: Array<[string, SavedSearchType[]]>
} {
    const next: Partial<Record<string, SavedSearchType[]>> = { ...settings }
    const pendingPosts: Array<[string, SavedSearchType[]]> = []

    for (const scope of SearchScopeSchema.options) {
        const list = parseList<SavedSearchType>(settings[scope])
        if (list.length === 0) continue

        let scopeChanged = false
        const upgraded = list.map((entry) => {
            const items = entry?.state?.filter?.filterModel?.items
            if (!Array.isArray(items) || items.length === 0) return entry
            if (!items.some((item) => item.field === 'filterIsBgaActive')) return entry
            scopeChanged = true
            return {
                ...entry,
                state: {
                    ...entry.state,
                    filter: {
                        ...entry.state!.filter,
                        filterModel: {
                            ...entry.state!.filter!.filterModel!,
                            items: items.map((item) =>
                                item.field === 'filterIsBgaActive'
                                    ? { ...item, field: 'filterIsAgencyActive' }
                                    : item,
                            ),
                        },
                    },
                },
            }
        })

        if (scopeChanged) {
            next[scope] = upgraded
            pendingPosts.push([scope, upgraded])
        }
    }

    return { next, pendingPosts }
}

export function renameUnnamedRoyaltyView(settings: Partial<Record<string, SavedSearchType[]>>): {
    next: Partial<Record<string, SavedSearchType[]>>
    pendingPosts: Array<[string, SavedSearchType[]]>
} {
    const next: Partial<Record<string, SavedSearchType[]>> = { ...settings }
    const pendingPosts: Array<[string, SavedSearchType[]]> = []
    const legacyKey = 'agreementListState'
    const newKey = 'searchStatesAgreement'
    const targetName = 'my saved royalty view'

    const rawLegacy = (settings as Record<string, unknown>)[legacyKey]
    if (!rawLegacy) return { next, pendingPosts }

    let parsedLegacy: unknown = rawLegacy
    if (typeof rawLegacy === 'string') {
        try {
            parsedLegacy = JSON.parse(rawLegacy)
        } catch {
            return { next, pendingPosts }
        }
    }
    if (Array.isArray(parsedLegacy)) return { next, pendingPosts }
    if (!parsedLegacy || typeof parsedLegacy !== 'object') return { next, pendingPosts }

    const legacyState = parsedLegacy as GridInitialState
    const existing = parseList<SavedSearchType>(settings[newKey])
    if (existing.some((entry) => entry.name === targetName)) {
        ;(next as Record<string, unknown>)[legacyKey] = []
        pendingPosts.push([legacyKey, []])
        return { next, pendingPosts }
    }

    const renamed: SavedSearchType = { name: targetName, state: legacyState }
    const updated = [...existing, renamed]
    next[newKey] = updated
    pendingPosts.push([newKey, updated])
    ;(next as Record<string, unknown>)[legacyKey] = []
    pendingPosts.push([legacyKey, []])
    return { next, pendingPosts }
}
